import React from 'react'
import { Link } from 'react-router-dom'
import { Dropdown, Input, Icon, Popup } from 'semantic-ui-react'
import DN_LOGO_LARGE from '../../assets/draynow-logo-color.svg'
import DN_LOGO_SMALL from '../../assets/draynow-logo-small.svg'
import MV_LOGO from '../../assets/modalview-logo-light.png'
import SupportModal from '../Support/SupportModal/SupportModal.jsx'
import { MarketSelector } from '../MarketSelector/MarketSelector.jsx'
import './NavBar.scss'

import PropTypes from 'prop-types'

export const NavBarView = ({
  isSidebarShown,
  onToggleSidebar,
  // From logic hook
  search,
  showHelp,
  showSearch,
  isAdmin,
  isDrayNow,
  hasActiveSearch,
  user,
  privacyPolicyUrl,
  termsConditionsUrl,
  showContactSupport,
  contactStrategy,
  location,
  searchConfig,
  handleSearchChange,
  toggleHelpModal,
  handleHelpClick,
  handleSubmitSearch,
  handleSearchBlur,
  clearSearch,
  logout,
}) => {
  const getPrivacyPolicyLink = () => {
    if (privacyPolicyUrl) {
      return {
        as: 'a',
        href: privacyPolicyUrl,
        target: '_blank',
        rel: 'noopener',
        key: 'privacy-policy',
        text: 'Privacy Policy',
        icon: 'file',
      }
    }
  }

  const getTermsConditionsLink = () => {
    if (termsConditionsUrl) {
      return {
        as: 'a',
        href: termsConditionsUrl,
        target: '_blank',
        rel: 'noopener',
        key: 'terms-and-conditions',
        text: 'Terms of Service',
        icon: 'file alternate',
      }
    }
  }

  return (
    <>
      <nav className='navbar'>
        <div className='navbar__content'>
          <div className='navbar__left'>
            <div className='toggle-navbar'>
              <Popup
                trigger={
                  <Icon
                    name={
                      isSidebarShown
                        ? 'angle double left'
                        : 'angle double right'
                    }
                    onClick={onToggleSidebar}
                    data-testid='navbar-sidebar-toggle-btn'
                  />
                }
                content={isSidebarShown ? 'Hide sidebar' : 'Show sidebar'}
                position='right center'
                size='mini'
              />
            </div>
            <Link to='/' className='navbar__brand'>
              {isDrayNow ? (
                <>
                  <Popup
                    trigger={
                      <img
                        src={DN_LOGO_LARGE}
                        className='logo logo--large'
                        alt='DrayNow logo'
                      />
                    }
                    content='DrayNow'
                    position='bottom center'
                    size='mini'
                  />
                  <Popup
                    trigger={
                      <img
                        src={DN_LOGO_SMALL}
                        className='logo logo--small'
                        alt='DrayNow logo'
                      />
                    }
                    content='DrayNow'
                    position='bottom center'
                    size='mini'
                  />
                </>
              ) : (
                <Popup
                  trigger={
                    <img
                      src={MV_LOGO}
                      style={{ height: 30 }}
                      alt='ModalView logo'
                    />
                  }
                  content='ModalView'
                  position='bottom center'
                  size='mini'
                />
              )}
            </Link>
          </div>
          {showSearch && searchConfig && (
            <form className='navbar__search' onSubmit={handleSubmitSearch}>
              <Input
                data-automation-id='searchBar'
                data-testid='navbar-search-input'
                value={search}
                placeholder={searchConfig.placeholder}
                label={searchConfig.label}
                onChange={handleSearchChange}
                onBlur={handleSearchBlur}
                icon={
                  hasActiveSearch && (
                    <Icon
                      name='close'
                      id='clearsearch'
                      className='clear-search'
                      inverted
                      circular
                      link
                      onClick={clearSearch}
                      data-testid='navbar-search-clear-btn'
                    />
                  )
                }
              />
            </form>
          )}
          <div className='navbar__right'>
            {(location?.pathname?.includes('/marketplace') ||
              location?.pathname?.includes('/containers')) && (
              <MarketSelector />
            )}
            <Dropdown
              data-testid='navbar-user-dropdown-btn'
              trigger={
                <span data-automation-id='user-selector'>
                  <Icon className='green' name='user' /> {user?.firstName}{' '}
                  {user?.lastName}
                </span>
              }
              options={[
                ...(isAdmin
                  ? [
                      {
                        as: Link,
                        to: '/admin',
                        key: 'admin',
                        text: 'Admin',
                        icon: 'user secret',
                        'data-testid': 'navbar-admin-link',
                      },
                    ]
                  : []),
                ...(showContactSupport
                  ? [
                      {
                        key: 'help',
                        text: 'Help',
                        icon: 'help',
                        onClick: handleHelpClick,
                        'data-testid': 'navbar-help-link',
                      },
                    ]
                  : []),
                ...(privacyPolicyUrl ? [getPrivacyPolicyLink()] : []),
                ...(termsConditionsUrl ? [getTermsConditionsLink()] : []),
                {
                  key: 'log-out',
                  text: 'Log Out',
                  icon: 'sign out',
                  onClick: logout,
                  'data-automation-id': 'log-out-button',
                  'data-testid': 'navbar-logout-btn',
                },
              ]}
              pointing='top right'
            />
          </div>
        </div>
      </nav>
      {showHelp && contactStrategy === 'custom' && (
        <SupportModal open onClose={toggleHelpModal} />
      )}
    </>
  )
}

NavBarView.propTypes = {
  isSidebarShown: PropTypes.bool,
  onToggleSidebar: PropTypes.func,
  search: PropTypes.string,
  showHelp: PropTypes.bool,
  showSearch: PropTypes.bool,
  isAdmin: PropTypes.bool,
  isDrayNow: PropTypes.bool,
  hasActiveSearch: PropTypes.bool,
  user: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
  }),
  privacyPolicyUrl: PropTypes.string,
  termsConditionsUrl: PropTypes.string,
  showContactSupport: PropTypes.bool,
  contactStrategy: PropTypes.oneOf(['pending', 'none', 'intercom', 'custom']),
  location: PropTypes.shape({
    pathname: PropTypes.string,
  }),
  searchConfig: PropTypes.shape({
    placeholder: PropTypes.string,
    label: PropTypes.object,
  }),
  handleSearchChange: PropTypes.func,
  toggleHelpModal: PropTypes.func,
  handleHelpClick: PropTypes.func,
  handleSubmitSearch: PropTypes.func,
  handleSearchBlur: PropTypes.func,
  clearSearch: PropTypes.func,
  logout: PropTypes.func,
}
